import { createTheme } from "@mui/material/styles"

interface PaletteElementsOptions {
  main: {
    background: string
    text: string
    subText: string
    border: string
  }
  videoControls: {
    background: string
    text: string
    subText: string
    border: string
  }
  header: {
    background: string
    icon: string
    border: string
  }
  footer: {
    background: string
    icon: string
    text: string
    subText: string
    border: string
  }
  searchBar: {
    background: string
    backgroundHover: string
    icon: string
    results?: {
      background: string
      hoverBackground: string
    }
  }
  sideBar: {
    background: string
    icon: string
    categoryTitle: string
    text: string
    border: string
    buttonHover: string
    buttonHoverBorder: string
    alpha: number
  }
  playlist: {
    header: string
    current: string
  }
  login?: {
    background: string
  }
  contextMenu?: {
    background: string
  }
  motd: {
    background: string
    color?: string
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    like: Palette["primary"]
    soft: Palette["primary"]
    element: PaletteElementsOptions
  }

  interface PaletteOptions {
    like?: PaletteOptions["primary"]
    soft?: PaletteOptions["primary"]
    element: PaletteElementsOptions
  }

  interface Theme {
    name?: string
  }

  interface ThemeOptions {
    name?: string
  }
}

declare module "@mui/material/InputBase" {
  interface InputBasePropsColorOverrides {
    like: true
    soft: true
  }
}

declare module "@mui/material/InputLabel" {
  interface InputLabelPropsColorOverrides {
    like: true
    soft: true
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    like: true
    soft: true
  }
}

declare module "@mui/material/Checkbox" {
  interface CheckboxPropsColorOverrides {
    like: true
    soft: true
  }
}

export const defaultTheme = createTheme({
  name: "default",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#060606", default: "#000" },
    primary: {
      light: "#333333",
      main: "#222222",
      dark: "#111111",
      contrastText: "#fff",
    },
    secondary: {
      main: "#4A90E2",
      contrastText: "#fff",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#0b0b0b",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#111111",
        text: "#fff",
        subText: "#808080",
        border: "#D3D3D3",
      },
      header: {
        background: "#00132B",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#00132B",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#0C315E",
        backgroundHover: "#0E4484",
        icon: "#fff",
      },
      sideBar: {
        background: "#00132B",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#2663a9",
        buttonHoverBorder: "#fff",
        alpha: 0.4,
      },
      playlist: {
        header: "#222222",
        current: "#0C315E",
      },
      motd: {
        background: "#0C315E",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const lightTheme = createTheme({
  name: "light",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#e8e8e8", default: "#fff" },
    primary: {
      dark: "#cccccc",
      main: "#dddddd",
      light: "#eeeeee",
      contrastText: "#000",
    },
    secondary: {
      main: "#4A90E2",
      contrastText: "#fff",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#000000",
      secondary: "#505050",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "light",
    element: {
      main: {
        background: "#EDEDED",
        text: "#000",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#dddddd",
        text: "#000",
        subText: "#808080",
        border: "#aaaaaa",
      },
      header: {
        background: "#fff",
        icon: "#000",
        border: "#000",
      },
      footer: {
        background: "#fff",
        icon: "#fff",
        border: "#000",
        text: "#000",
        subText: "#808080",
      },
      searchBar: {
        background: "#dddddd",
        backgroundHover: "#eeeeee",
        icon: "#000",
      },
      sideBar: {
        background: "#fff",
        icon: "#666",
        categoryTitle: "#808080",
        text: "#000",
        border: "#808080",
        buttonHover: "#4a90e2",
        buttonHoverBorder: "#000",
        alpha: 1,
      },
      playlist: {
        header: "#dddddd",
        current: "#fff",
      },
      motd: {
        background: "#dddddd",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const christmasTheme = createTheme({
  name: "christmas",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#060606", default: "#000" },
    primary: {
      light: "#333333",
      main: "#222222",
      dark: "#111111",
      contrastText: "#fff",
    },
    secondary: {
      main: "#31b92aff",
      contrastText: "#ffffffff",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#0b0b0b",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#111111",
        text: "#fff",
        subText: "#826b6bff",
        border: "#D3D3D3",
      },
      header: {
        background: "#2b0000ff",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#2b0000ff",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#5e1a0cff",
        backgroundHover: "#841a0eff",
        icon: "#fff",
      },
      sideBar: {
        background: "#022e00ff",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#4a9f45ff",
        buttonHoverBorder: "#fff",
        alpha: 0.4,
      },
      playlist: {
        header: "#222222",
        current: "#5e140cff",
      },
      motd: {
        background: "#5e140cff",
        color: "#fff",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const halloweenTheme = createTheme({
  name: "halloween",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#060606", default: "#000" },
    primary: {
      light: "#333333",
      main: "#222222",
      dark: "#111111",
      contrastText: "#fff",
    },
    secondary: {
      main: "#e78a33",
      contrastText: "#fff",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#0b0b0b",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#111111",
        text: "#fff",
        subText: "#808080",
        border: "#D3D3D3",
      },
      header: {
        background: "#2b1500",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#2b1500",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#63360c",
        backgroundHover: "#8b5210",
        icon: "#fff",
      },
      sideBar: {
        background: "#2b1500",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#a95d26",
        buttonHoverBorder: "#fff",
        alpha: 0.4,
      },
      playlist: {
        header: "#222222",
        current: "#63360c",
      },
      motd: {
        background: "#63360c",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const ultraDarkTheme = createTheme({
  name: "ultraDark",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#060606", default: "#000" },
    primary: {
      light: "#101010",
      main: "#000000",
      dark: "#000000",
      contrastText: "#fff",
    },
    secondary: {
      main: "#4A90E2",
      contrastText: "#fff",
    },
    soft: {
      main: "#444",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#000000",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#000000",
        text: "#fff",
        subText: "#808080",
        border: "#787878ff",
      },
      header: {
        background: "#000000",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#000000",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#1e1e1eff",
        backgroundHover: "#282828ff",
        icon: "#fff",
      },
      sideBar: {
        background: "#000000",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#2663a9",
        buttonHoverBorder: "#fff",
        alpha: 0.4,
      },
      playlist: {
        header: "#000000",
        current: "#1e1e1eff",
      },
      motd: {
        background: "#1e1e1eff",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const glassTheme = createTheme({
  name: "glass",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#060606", default: "#000" },
    primary: {
      light: "#333333",
      main: "#222222",
      dark: "#111111",
      contrastText: "#fff",
    },
    secondary: {
      main: "#4A90E2",
      contrastText: "#fff",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#0b0b0b",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#111111",
        text: "#fff",
        subText: "#808080",
        border: "#D3D3D3",
      },
      header: {
        background: "#00132B33",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#00132B",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#0C315E33",
        backgroundHover: "#0E448433",
        icon: "#fff",
        results: {
          background: "#00000033",
          hoverBackground: "#00000088",
        },
      },
      sideBar: {
        background: "#00132B",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#2663a9",
        buttonHoverBorder: "#fff",
        alpha: 0.4,
      },
      playlist: {
        header: "#222222",
        current: "#0C315E",
      },
      login: {
        background: "#00000033",
      },
      contextMenu: {
        background: "#00000033",
      },
      motd: {
        background: "#00000033",
      },
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h3: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
    },
    h1: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
    },
    h5: {
      fontFamily: [
        "Avant Garde",
        "Avantgarde",
        "Century Gothic",
        "CenturyGothic",
        "AppleGothic",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
    },
  },
})

export const classicTheme = createTheme({
  name: "classic",
  palette: {
    common: { black: "#000", white: "#fff" },
    background: { paper: "#101010", default: "#000" },
    primary: {
      light: "#333333",
      main: "#222222",
      dark: "#111111",
      contrastText: "#fff",
    },
    secondary: {
      main: "#cdcdcd",
      contrastText: "#303030",
    },
    soft: {
      main: "#888",
      contrastText: "#fff",
    },
    like: {
      main: "#519F51",
      dark: "#3D803D",
      contrastText: "#fff",
    },
    error: {
      light: "rgba(255, 0, 0, 1)",
      main: "rgba(255, 0, 0, 1)",
      dark: "rgba(255, 0, 0, 1)",
      contrastText: "#fff",
    },
    text: {
      primary: "#fff",
      secondary: "#808080",
      disabled: "rgba(97, 97, 97, 1)",
    },
    mode: "dark",
    element: {
      main: {
        background: "#0b0b0b",
        text: "#fff",
        subText: "#808080",
        border: "#A9A9A9",
      },
      videoControls: {
        background: "#111111",
        text: "#fff",
        subText: "#808080",
        border: "#D3D3D3",
      },
      header: {
        background: "#101010",
        icon: "#fff",
        border: "#fff",
      },
      footer: {
        background: "#101010",
        icon: "#fff",
        border: "#fff",
        text: "#fff",
        subText: "#808080",
      },
      searchBar: {
        background: "#9D9D9D",
        backgroundHover: "#9D9D9D",
        icon: "#303030",
      },
      sideBar: {
        background: "#101010",
        icon: "#D3D3D3",
        categoryTitle: "#808080",
        text: "#fff",
        border: "#808080",
        buttonHover: "#505050",
        buttonHoverBorder: "#fff",
        alpha: 1,
      },
      playlist: {
        header: "#222222",
        current: "#505050",
      },
      motd: {
        background: "#505050",
      },
    },
  },
  typography: {
    fontFamily: ["Verdana", "sans-serif"].join(","),
    h5: {
      fontWeight: 600,
    },
  },
})
